import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import moment from "moment";

import "./Header.scss";
import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import CardItem from "pages/shipper/RequestForm/components/Header/CardItem";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import { getPackingListCalculations } from "helpers/quotationFormHelper";
import { QuotationFreightModeOption } from "types/Common";
import { IQuotationData } from "pages/provider/QuotationDetail/QuotationType";
import TopRow from "./TopRow";
import { QuotationFormState } from "../../QuotationFormState";

interface IHeaderProps {
  quoteRequestDetails: IQuotationData;
  state: QuotationFormState;
  modifiedDate?: string | null;
}

function Header(props: IHeaderProps) {
  const { state, quoteRequestDetails, modifiedDate } = props;
  const { watch } = useFormContext();
  const id = useParams().id?.toUpperCase();

  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { totalWeight, weightUnit, listItems } = getPackingListCalculations(quoteRequestDetails) ?? {};

  const isPickupReady = quoteRequestDetails?.schedule?.pickup_cargo_ready;
  const isDeliveryReady = quoteRequestDetails?.schedule?.delivery_cargo_ready;

  const freightModeOptions: QuotationFreightModeOption[] = quotationConfigData?.freight_modes ?? [];
  const selectedFreightModeObject = freightModeOptions.find((mode) => mode.value === watch("service.freight_mode"));
  const SelectedFreightModeIcon = getFreightModeIcon(
    selectedFreightModeObject?.label,
    selectedFreightModeObject?.type,
    true,
  );

  const headerCards = [
    {
      title: <FormattedMessage id="general.freightMode" defaultMessage="Freight Mode" />,
      children: (
        <>
          {selectedFreightModeObject ? (
            <OverlayTrigger
              placement="top"
              key={selectedFreightModeObject?.label}
              overlay={
                <Tooltip id="freight-mode" style={{ zIndex: 999999 }}>
                  <span className="font-weight-normal">{selectedFreightModeObject?.label}</span>
                </Tooltip>
              }
            >
              <SelectedFreightModeIcon />
            </OverlayTrigger>
          ) : null}
        </>
      ),
    },
    {
      title: <FormattedMessage id="general.pickup" defaultMessage="Pickup" />,
      children: (
        <>
          {quoteRequestDetails?.schedule?.pickup_country && quoteRequestDetails?.schedule?.pickup_time ? (
            <>
              <p className="font-b4">{quoteRequestDetails?.schedule?.pickup_country}</p>
              <p className="font-b3">
                {isPickupReady === "asap_pickup" ? (
                  <span>
                    <FormattedMessage id=" new-request.schedule.defaultCargoReadyOption.asap" defaultMessage="ASAP" />
                  </span>
                ) : (
                  <>
                    <span> {moment(quoteRequestDetails?.schedule?.pickup_time, "HH:mm:ss").format("HH:mm a")}</span>
                    <span> {moment(quoteRequestDetails?.schedule?.pickup_date).format("DD MMM ")}</span>
                  </>
                )}
              </p>
            </>
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      title: <FormattedMessage id="general.delivery" defaultMessage="Delivery" />,
      children: (
        <>
          {quoteRequestDetails?.schedule?.stop1_country && quoteRequestDetails?.schedule?.stop1_time ? (
            <>
              <p className="font-b4">{quoteRequestDetails?.schedule?.stop1_country}</p>
              <p className="font-b3">
                {isDeliveryReady === "asap_stop1" ? (
                  <span>
                    <FormattedMessage id=" new-request.schedule.defaultCargoReadyOption.asap" defaultMessage="ASAP" />
                  </span>
                ) : (
                  <>
                    <span> {moment(quoteRequestDetails?.schedule?.stop1_time, "HH:mm:ss").format("HH:mm a")}</span>
                    <span> {moment(quoteRequestDetails?.schedule?.stop1_date).format("DD MMM ")}</span>
                  </>
                )}
              </p>
            </>
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      title: <FormattedMessage id="general.shipperName" defaultMessage="Shipper name" />,
      children: (
        <>
          <p className="font-b4">{quoteRequestDetails?.organization?.shipper}</p>
          <p className="font-b3">{quoteRequestDetails?.organization?.payment_location}</p>
        </>
      ),
    },
    {
      title: <FormattedMessage id="general.team" defaultMessage="Team" />,
      children: <p className="font-b4">{quotationConfigData?.assigned_team?.team_name}</p>,
    },
    {
      title: <FormattedMessage id="general.totalWeight" defaultMessage="Total weight" />,
      children: (
        <>
          <p className="font-b4">
            {totalWeight}
            {weightUnit}
          </p>
          <p className="font-b3">{listItems?.join(", ")}</p>
        </>
      ),
    },
    {
      title: <FormattedMessage id="general.totalAmount" defaultMessage="Total amount" />,
      children: <p className="font-b4">{state.costs.totalAmount}</p>,
    },
  ];

  return (
    <Card className="mb-3 form-container quotation-form-header" id="formHeader">
      <TopRow freightModeOptions={freightModeOptions} numberOfStops={quoteRequestDetails?.schedule?.stops?.length || 0} />
      <div className="d-flex flex-row pl-16 header-card-container">
        {headerCards.map((card, i) => (
          <CardItem key={i}>
            <p className="font-b3 text-secondary mb-2 text-capitalize">{card.title}</p>
            {card.children}
          </CardItem>
        ))}
      </div>
      {modifiedDate && (
        <div className="d-flex justify-content-end text-secondary font-b6 mr-2xl mb-lg">
          Auto saved {formatDate(modifiedDate, DateTimeFormat.fullDateTimeFormat)}
        </div>
      )}
    </Card>
  );
}

Header.defaultProps = {
  modifiedDate: null,
};

export default Header;
